import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api, errText, photoUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Skeleton } from "../../components/ui/skeleton";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../components/ui/alert-dialog";
import { toast } from "sonner";
import { CreateUserDialog } from "./AdminCreate";
import { STATUS_STYLE } from "./adminShared";
import { sellerTypeClass } from "../../lib/badgeColors";
import { Clock, Plus, MessageCircle, Users, RotateCcw, PlayCircle, PauseCircle, BarChart3, Download, BadgeCheck, BadgeX, MoreVertical, Pencil, Eye, LogOut, Ban, Trash2, Loader2, Send, ShieldCheck, AlertTriangle, Pin } from "lucide-react";
import { TRUST_CLS, timeAgo } from "../../components/admin-users/shared";
import { Sparkline, PresenceAnalytics } from "../../components/admin-users/PresenceAnalytics";
import { TrustBandsDialog } from "../../components/admin-users/TrustBandsDialog";
import { UserPeek } from "../../components/admin-users/UserPeek";
import { MemberConversationsDialog } from "../../components/admin-users/MemberConversationsDialog";
import { EditUserDialog } from "../../components/admin-users/EditUserDialog";
import { CampaignDrillDialog, QuietHoursDialog } from "../../components/admin-users/UserDialogs";

export function UsersManager() {
  const { impersonate, user: me } = useAuth();
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [flashIds, setFlashIds] = useState([]);
  const prevPresenceRef = useRef({});
  const flashTimerRef = useRef(null);
  const load = () => api.get(`/admin/users?q=${encodeURIComponent(qRef.current)}`).then((r) => {
    const rows = r.data;
    const prev = prevPresenceRef.current;
    const changed = rows.filter((u) => prev[u.user_id] !== undefined && prev[u.user_id] !== u.presence).map((u) => u.user_id);
    prevPresenceRef.current = Object.fromEntries(rows.map((u) => [u.user_id, u.presence]));
    setUsers(rows);
    if (changed.length) {
      setFlashIds(changed);
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashIds([]), 2600);
    }
  });
  const qRef = useRef("");
  useEffect(() => { qRef.current = q; }, [q]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const [editUser, setEditUser] = useState(null);
  const [unverifyTarget, setUnverifyTarget] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null); // {type:'single', u} | {type:'bulk', ids, count} | {type:'filter'}
  const [msgText, setMsgText] = useState("");
  const [msgEmail, setMsgEmail] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);
  const [selected, setSelected] = useState([]);
  const [sortByTrust, setSortByTrust] = useState(false);
  const [riskyOnly, setRiskyOnly] = useState(false);
  const [unreadMsgOnly, setUnreadMsgOnly] = useState(false);
  const [convUser, setConvUser] = useState(null);
  const [presence, setPresence] = useState(() => localStorage.getItem("cp_users_presence") || "all");
  const [pinnedPresence, setPinnedPresence] = useState(() => localStorage.getItem("cp_users_presence") || "");
  const [heartbeat, setHeartbeat] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [bandsOpen, setBandsOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [unreadIds, setUnreadIds] = useState([]);
  const [schedOn, setSchedOn] = useState(false);
  const [schedAt, setSchedAt] = useState("");
  const [filters, setFilters] = useState({ seller_type: "any", verified: "any", status: "any", province: "any" });
  const [audCount, setAudCount] = useState(null);
  const [scheduledMsgs, setScheduledMsgs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [recurrence, setRecurrence] = useState("none");
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [drillCampaign, setDrillCampaign] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [excludeIds, setExcludeIds] = useState([]);
  const [quiet, setQuiet] = useState(null);
  const [quietOpen, setQuietOpen] = useState(false);
  const loadQuiet = () => api.get("/admin/quiet-hours").then((r) => setQuiet(r.data)).catch(() => {});
  const openQuiet = () => setQuietOpen(true);
  const loadTemplates = () => api.get("/admin/reply-templates").then((r) => setTemplates(r.data)).catch(() => {});
  const loadScheduled = () => api.get("/admin/scheduled-messages").then((r) => setScheduledMsgs(r.data)).catch(() => {});
  const loadCampaigns = () => api.get("/admin/message-campaigns").then((r) => setCampaigns(r.data)).catch(() => {});
  const loadAudiences = () => api.get("/admin/saved-audiences").then((r) => setSavedAudiences(r.data)).catch(() => {});
  const loadUnread = () => api.get("/admin/users/unread-replies").then((r) => setUnreadIds(r.data.user_ids || [])).catch(() => {});
  const loadHeartbeat = () => api.get("/admin/presence/history").then((r) => setHeartbeat(r.data)).catch(() => {});
  useEffect(() => { loadTemplates(); loadScheduled(); loadCampaigns(); loadAudiences(); loadQuiet(); loadUnread(); loadHeartbeat(); const t = setInterval(() => { loadUnread(); loadHeartbeat(); }, 30000); return () => clearInterval(t); }, []);

  const togglePinPresence = () => {
    if (pinnedPresence === presence) {
      localStorage.removeItem("cp_users_presence");
      setPinnedPresence("");
      toast.success("Default view cleared");
    } else {
      localStorage.setItem("cp_users_presence", presence);
      setPinnedPresence(presence);
      toast.success(`Users list will now open on "${presence === "all" ? "All users" : presence.charAt(0).toUpperCase() + presence.slice(1)}"`);
    }
  };

  const resetCompose = () => { setMsgText(""); setMsgEmail(false); setSchedOn(false); setSchedAt(""); setRecurrence("none"); };

  const saveAudience = async () => {
    const name = window.prompt("Name this audience (e.g. Unverified breeders in Ontario):");
    if (!name) return;
    try { await api.post("/admin/saved-audiences", { name, filters }); toast.success("Audience saved"); loadAudiences(); }
    catch (e) { toast.error(errText(e)); }
  };
  const loadSavedAudience = (a) => { setFilters(a.filters); refreshAudCount(a.filters); };
  const openPreview = async () => {
    setPreviewOpen(true); setPreviewRows(null);
    try {
      const f = filters;
      const r = await api.get(`/admin/users/audience-preview?seller_type=${f.seller_type}&verified=${f.verified}&status=${f.status}&province=${f.province}`);
      setPreviewRows(r.data.users);
    } catch (e) { toast.error(errText(e)); setPreviewOpen(false); }
  };  const deleteAudience = async (id) => {
    try { await api.delete(`/admin/saved-audiences/${id}`); loadAudiences(); } catch (e) { toast.error(errText(e)); }
  };

  const openDrill = (c) => setDrillCampaign(c);
  const exportCampaign = (c) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/admin/message-campaigns/${c.id}/export.csv`, "_blank");
  };
  const openMsgSingle = (u) => { setMsgTarget({ type: "single", u }); resetCompose(); };
  const openMsgBulk = () => { setMsgTarget({ type: "bulk", ids: selected, count: selected.length }); resetCompose(); };
  const openMsgOnline = () => {
    const ids = users.filter((u) => u.online && u.user_id !== me?.user_id).map((u) => u.user_id);
    if (!ids.length) { toast.info("No other users are online right now"); return; }
    setMsgTarget({ type: "bulk", ids, count: ids.length }); resetCompose();
  };
  const openMsgFilter = () => { setMsgTarget({ type: "filter" }); resetCompose(); setExcludeIds([]); refreshAudCount(filters); };

  const refreshAudCount = (f) => {
    api.get(`/admin/users/audience-count?seller_type=${f.seller_type}&verified=${f.verified}&status=${f.status}&province=${f.province}`)
      .then((r) => setAudCount(r.data.count)).catch(() => setAudCount(null));
  };
  const setFilter = (k, v) => { const f = { ...filters, [k]: v }; setFilters(f); setExcludeIds([]); refreshAudCount(f); };

  const applyTemplate = (tpl) => {
    const first = msgTarget?.type === "single" ? (msgTarget.u.name || "there").split(" ")[0] : "there";
    setMsgText((tpl.body || "").replace(/\{first\}/g, first));
  };

  const saveTemplate = async () => {
    if (!msgText.trim()) return;
    const label = window.prompt("Save this message as a template. Template name:");
    if (!label) return;
    try { await api.post("/admin/reply-templates", { label, body: msgText.trim() }); toast.success("Template saved"); loadTemplates(); }
    catch (e) { toast.error(errText(e)); }
  };

  const sendMessage = async () => {
    if (!msgText.trim()) return;
    if (schedOn && !schedAt) { toast.error("Pick a date & time to schedule"); return; }
    const scheduled_at = schedOn ? new Date(schedAt).toISOString() : "";
    setSendingMsg(true);
    try {
      let res;
      if (msgTarget.type === "bulk") {
        res = await api.post("/admin/users/message-bulk", { user_ids: msgTarget.ids, message: msgText.trim(), email: msgEmail, scheduled_at });
        setSelected([]);
      } else if (msgTarget.type === "filter") {
        res = await api.post("/admin/users/message-filter", { filters, message: msgText.trim(), email: msgEmail, scheduled_at, recurrence: schedOn ? recurrence : "none", exclude_ids: excludeIds });
      } else {
        res = await api.post(`/admin/users/${msgTarget.u.user_id}/message`, { message: msgText.trim(), email: msgEmail, scheduled_at });
      }
      if (res.data.status === "scheduled") {
        const rec = res.data.recurrence && res.data.recurrence !== "none" ? ` · repeats ${res.data.recurrence}` : "";
        toast.success(`Scheduled for ${new Date(res.data.scheduled_at).toLocaleString()} · ${res.data.count} recipient${res.data.count === 1 ? "" : "s"}${rec}`);
        loadScheduled();
      } else if (res.data.status === "held") {
        toast.success(`🌙 Held for quiet hours — will send ${new Date(res.data.scheduled_at).toLocaleString()} · ${res.data.count} recipient${res.data.count === 1 ? "" : "s"}`);
        loadScheduled();
      } else if (res.data.status === "partial") {
        toast.success(`Sent to ${res.data.sent} now · 🌙 ${res.data.held} held for quiet hours (their local night)`);
        loadScheduled();
        if (res.data.campaign_id) loadCampaigns();
      } else {
        const n = res.data.sent ?? 1;
        toast.success(`Message sent to ${n} user${n === 1 ? "" : "s"}${msgEmail && res.data.emailed ? ` · ${res.data.emailed} emailed` : ""}`);
        if (res.data.campaign_id) loadCampaigns();
      }
      setMsgTarget(null); resetCompose(); loadUnread();
    } catch (e) { toast.error(errText(e)); }
    finally { setSendingMsg(false); }
  };

  const cancelScheduled = async (id) => {
    try { await api.delete(`/admin/scheduled-messages/${id}`); toast.success("Cancelled"); loadScheduled(); }
    catch (e) { toast.error(errText(e)); }
  };
  const togglePause = async (m) => {
    try {
      await api.post(`/admin/scheduled-messages/${m.id}/${m.status === "paused" ? "resume" : "pause"}`);
      toast.success(m.status === "paused" ? "Resumed" : "Paused");
      loadScheduled();
    } catch (e) { toast.error(errText(e)); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const loginAsUser = async (u) => {
    try {
      await impersonate(u.user_id);
      toast.success(`Now viewing as ${u.name}`);
      window.location.href = "/dashboard";
    } catch (e) { toast.error(errText(e)); }
  };

  const changeRole = async (id, role) => {
    try { await api.post(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const makeVerifiedSeller = async (u) => {
    if (u.verified_seller) { toast.info(`${u.name} is already a verified seller`); return; }
    try {
      await api.post(`/admin/users/${u.user_id}/verify`, { verified_seller: true });
      toast.success(`${u.name} is now a Verified Seller`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const makeVerifiedBusiness = async (u) => {
    if (u.verified_business) { toast.info(`${u.name} is already a verified business`); return; }
    try {
      await api.post(`/admin/users/${u.user_id}/verify`, { verified_business: true });
      toast.success(`${u.name} is now a Verified Business — they can access the Advertise flow`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const makeVerifiedBreeder = async (u) => {
    if (u.verified_breeder) { toast.info(`${u.name} is already a verified breeder`); return; }
    try {
      await api.post(`/admin/users/${u.user_id}/verify`, { verified_breeder: true });
      toast.success(`${u.name} is now a Verified Breeder`);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const removeVerification = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/verify`, { verified_seller: false, verified_business: false, verified_breeder: false });
      toast.success(`Verification removed from ${u.name}`);
      setUnverifyTarget(null);
      load();
    } catch (e) { toast.error(errText(e)); }
  };
  const changeStatus = async (id, status) => {
    try { await api.post(`/admin/users/${id}/status`, { status }); toast.success(`User ${status}`); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const forceLogout = async (u) => {
    try { await api.post(`/admin/staff/${u.user_id}/force-logout`); toast.success(`Ended ${u.name}'s active sessions`); }
    catch (e) { toast.error(errText(e)); }
  };
  const removeUser = async (id) => {
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="user-search" placeholder="Search users…" className="flex-1 h-11 px-4 rounded-xl border border-slate-200 outline-none focus:border-[#F5551E]" />
        <Button onClick={load} className="rounded-xl bg-[#12234A] text-white">Search</Button>
        <Button onClick={openQuiet} variant="outline" data-testid="quiet-hours-btn" className={`rounded-xl gap-1.5 ${quiet?.enabled ? "border-indigo-300 text-indigo-600 bg-indigo-50" : ""}`}>
          <Clock size={16} /> Quiet hours{quiet?.enabled ? ` · ${String(quiet.start).padStart(2, "0")}–${String(quiet.end).padStart(2, "0")}` : " off"}
        </Button>
        <Button onClick={() => setCreateOpen(true)} data-testid="new-user-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5"><Plus size={16} /> New user</Button>
      </div>
      {users && users.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3 flex flex-wrap items-center gap-3" data-testid="users-bulk-bar">
          <label className="flex items-center gap-2 text-sm font-semibold text-[#12234A] cursor-pointer">
            <Checkbox
              checked={selected.length > 0 && selected.length === users.filter((u) => u.user_id !== me?.user_id).length}
              onCheckedChange={(v) => setSelected(v ? users.filter((u) => u.user_id !== me?.user_id).map((u) => u.user_id) : [])}
              data-testid="users-select-all"
            />
            {selected.length ? `${selected.length} selected` : "Select all"}
          </label>
          <button onClick={() => setSortByTrust((v) => !v)} data-testid="users-sort-trust" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${sortByTrust ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <ShieldCheck size={13} /> {sortByTrust ? "Riskiest first" : "Sort by trust"}
          </button>
          <button onClick={() => setRiskyOnly((v) => !v)} data-testid="users-filter-risky" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${riskyOnly ? "bg-red-600 text-white border-red-600" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <AlertTriangle size={13} /> Watch / High-risk only
          </button>
          <button onClick={() => setUnreadMsgOnly((v) => !v)} data-testid="users-filter-unread-msgs" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${unreadMsgOnly ? "bg-[#F5551E] text-white border-[#F5551E]" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <MessageCircle size={13} /> Unread member messages ({users.filter((u) => u.msg_unread > 0).length})
          </button>
          <Select value={presence} onValueChange={setPresence}>
            <SelectTrigger data-testid="users-presence-filter" className="h-9 w-[150px] rounded-xl text-xs font-semibold border-slate-200">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all" data-testid="presence-opt-all">All users ({users.length})</SelectItem>
              <SelectItem value="online" data-testid="presence-opt-online"><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online ({users.filter((u) => u.online).length})</span></SelectItem>
              <SelectItem value="offline" data-testid="presence-opt-offline"><span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-red-400" /> Offline ({users.filter((u) => !u.online).length})</span></SelectItem>
            </SelectContent>
          </Select>
          <button onClick={togglePinPresence} data-testid="pin-presence-default" title={pinnedPresence === presence && pinnedPresence ? "This is your default view — click to clear" : "Make this the default view when you open Users"} className={`text-xs font-semibold px-2.5 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${pinnedPresence && pinnedPresence === presence ? "bg-[#12234A] text-white border-[#12234A]" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <Pin size={13} className={pinnedPresence && pinnedPresence === presence ? "fill-current" : ""} /> {pinnedPresence && pinnedPresence === presence ? "Default" : "Set default"}
          </button>
          {users.some((u) => u.online && u.user_id !== me?.user_id) && (
            <Button onClick={openMsgOnline} data-testid="message-online-now" variant="outline" className="rounded-xl gap-1.5 h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Message online now ({users.filter((u) => u.online && u.user_id !== me?.user_id).length})
            </Button>
          )}
          {heartbeat?.points && <Sparkline points={heartbeat.points} peak={heartbeat.peak} />}
          <button onClick={() => setAnalyticsOpen((v) => !v)} data-testid="toggle-presence-analytics" className={`text-xs font-semibold px-3 py-1.5 rounded-xl border transition-colors flex items-center gap-1 ${analyticsOpen ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:text-[#12234A]"}`}>
            <BarChart3 size={13} /> {analyticsOpen ? "Hide analytics" : "Analytics"}
          </button>
          {me?.role === "admin" && (
            <button onClick={() => setBandsOpen(true)} data-testid="open-trust-bands" className="text-xs font-semibold px-3 py-1.5 rounded-xl border bg-white text-slate-500 border-slate-200 hover:text-[#12234A] transition-colors flex items-center gap-1">
              <ShieldCheck size={13} /> Trust bands
            </button>
          )}
          <Button onClick={openMsgBulk} disabled={!selected.length} data-testid="bulk-message" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5 h-9 disabled:opacity-40 ml-auto">
            <MessageCircle size={15} /> Message selected
          </Button>
          <Button onClick={openMsgFilter} variant="outline" data-testid="message-by-filter" className="rounded-xl gap-1.5 h-9 border-[#F5551E]/40 text-[#F5551E] hover:bg-[#FFF4F0]">
            <Users size={15} /> Message by filter
          </Button>
        </div>
      )}
      {analyticsOpen && <PresenceAnalytics />}
      <TrustBandsDialog open={bandsOpen} onClose={() => setBandsOpen(false)} onSaved={load} />
      {convUser && <MemberConversationsDialog user={convUser} onClose={() => setConvUser(null)} />}
      {scheduledMsgs.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 p-3 mb-3" data-testid="scheduled-messages">
          <h3 className="text-sm font-bold text-[#12234A] flex items-center gap-1.5 mb-2"><Clock size={15} className="text-amber-500" /> Scheduled messages</h3>
          <div className="space-y-1.5">
            {scheduledMsgs.map((m) => (
              <div key={m.id} className="flex items-center gap-3 text-sm" data-testid={`scheduled-msg-${m.id}`}>
                <Badge className={`${m.status === "sent" ? "bg-emerald-500" : m.status === "processing" ? "bg-sky-500" : m.status === "paused" ? "bg-slate-400" : "bg-amber-500"} text-white capitalize`}>{m.status}</Badge>
                <span className="text-slate-500 shrink-0">{new Date(m.scheduled_at).toLocaleString()}</span>
                {m.recurrence && m.recurrence !== "none" && <Badge variant="outline" className="text-[10px] capitalize gap-0.5"><RotateCcw size={10} /> {m.recurrence}</Badge>}
                <span className="text-slate-400">· {m.count} to {m.target_label}</span>
                <span className="flex-1 truncate text-slate-600">{m.message}</span>
                {(m.status === "scheduled" || m.status === "paused") && m.recurrence && m.recurrence !== "none" && (
                  <Button onClick={() => togglePause(m)} variant="ghost" size="sm" data-testid={`pause-scheduled-${m.id}`} className="h-7 gap-1 text-slate-600">
                    {m.status === "paused" ? <><PlayCircle size={12} /> Resume</> : <><PauseCircle size={12} /> Pause</>}
                  </Button>
                )}
                {(m.status === "scheduled" || m.status === "paused") && (
                  <Button onClick={() => cancelScheduled(m.id)} variant="ghost" size="sm" data-testid={`cancel-scheduled-${m.id}`} className="text-red-600 h-7">Cancel</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {campaigns.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-3" data-testid="message-campaigns">
          <h3 className="text-sm font-bold text-[#12234A] flex items-center gap-1.5 mb-2"><BarChart3 size={15} className="text-[#F5551E]" /> Message delivery receipts</h3>
          <div className="space-y-1.5">
            {campaigns.map((c) => (
              <div key={c.id} className="flex items-center gap-3 text-sm hover:bg-slate-50 rounded-lg px-1 py-0.5" data-testid={`campaign-${c.id}`}>
                <button onClick={() => openDrill(c)} data-testid={`campaign-drill-${c.id}`} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                  <span className="text-slate-400 shrink-0 w-28 truncate">{c.target_label}</span>
                  <span className="flex-1 truncate text-slate-600">{c.message}</span>
                  <span className="shrink-0 text-xs text-slate-500">📨 {c.reach} sent</span>
                  <span className="shrink-0 text-xs font-semibold text-sky-600" data-testid={`campaign-opened-${c.id}`}>👁 {c.opened} opened ({c.open_rate}%)</span>
                  <span className="shrink-0 text-xs font-semibold text-emerald-600" data-testid={`campaign-replied-${c.id}`}>↩ {c.replied} replied ({c.reply_rate}%)</span>
                </button>
                <Button onClick={() => exportCampaign(c)} variant="ghost" size="sm" data-testid={`campaign-export-${c.id}`} className="h-7 text-slate-500 gap-1 shrink-0"><Download size={13} /> CSV</Button>
              </div>
            ))}
          </div>
        </div>
      )}
      {!users ? <Skeleton className="h-40 rounded-2xl" /> : (
        <div className="space-y-2">
          {(() => {
            let list = riskyOnly ? users.filter((u) => (u.trust?.score ?? 100) < 40) : users;
            if (unreadMsgOnly) list = list.filter((u) => u.msg_unread > 0);
            if (presence === "online") list = list.filter((u) => u.online);
            else if (presence === "offline") list = list.filter((u) => !u.online);
            if (sortByTrust) list = [...list].sort((a, b) => (a.trust?.score ?? 100) - (b.trust?.score ?? 100));
            else if (unreadMsgOnly) list = [...list].sort((a, b) => (b.msg_unread || 0) - (a.msg_unread || 0));
            if (list.length === 0) return (
              <div className="text-center py-12 text-slate-400 text-sm" data-testid="users-empty">No {presence !== "all" ? presence : ""} users match the current filters.</div>
            );
            return list.map((u) => {
            const status = u.status || "active";
            return (
              <div key={u.user_id} className={`bg-white rounded-xl border p-3 flex items-center gap-3 transition-colors ${flashIds.includes(u.user_id) ? "presence-flash" : ""} ${selected.includes(u.user_id) ? "border-[#F5551E] bg-[#FFF7F4]" : "border-slate-200"}`} data-testid={`user-row-${u.user_id}`}>
                {u.user_id !== me?.user_id && <Checkbox checked={selected.includes(u.user_id)} onCheckedChange={() => toggleSel(u.user_id)} data-testid={`user-check-${u.user_id}`} />}
                <Link to={`/seller/${u.user_id}`} data-testid={`user-avatar-link-${u.user_id}`} className="shrink-0"><Avatar className="h-10 w-10"><AvatarImage src={photoUrl(u.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xs">{u.name?.[0]}</AvatarFallback></Avatar></Link>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#12234A] truncate flex items-center gap-1.5">
                    <UserPeek u={u} canAct={u.user_id !== me?.user_id} onMessage={openMsgSingle} onSuspend={(id) => changeStatus(id, "suspended")} onReactivate={(id) => changeStatus(id, "active")} onUnverify={setUnverifyTarget} isAdmin={me?.role === "admin"} onTrustChanged={load}>
                      <Link to={`/seller/${u.user_id}`} data-testid={`user-name-link-${u.user_id}`} className="hover:text-[#F5551E] hover:underline transition-colors">{u.name}</Link>
                    </UserPeek>
                    {u.seller_type && <span className={`${sellerTypeClass(u.seller_type)} text-[10px] font-semibold px-1.5 py-0.5 rounded-full`} data-testid={`user-type-${u.user_id}`}>{u.seller_type}</span>}
                    {(u.verified_seller || (u.is_verified_seller && !u.verified_business)) && <BadgeCheck size={14} className="text-emerald-500" title="Verified Seller" />}
                    {u.verified_business && <BadgeCheck size={14} className="text-amber-500" title="Verified Business" />}
                    {u.verified_breeder && <BadgeCheck size={14} className="text-purple-600" title="Verified Breeder" data-testid={`user-breeder-${u.user_id}`} />}
                    {unreadIds.includes(u.user_id) && (
                      <span data-testid={`user-reply-dot-${u.user_id}`} title="New reply" className="inline-flex items-center gap-1 text-[10px] font-bold text-[#F5551E] bg-[#FFF4F0] rounded-full px-1.5 py-0.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#F5551E]" /> replied
                      </span>
                    )}
                    {u.username && <span className="text-xs text-slate-400 font-normal">@{u.username}</span>}
                  </div>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                {u.msg_received > 0 && (u.msg_unread > 0 ? (
                  <button onClick={() => setConvUser(u)} data-testid={`user-msg-indicator-${u.user_id}`} title={`${u.msg_unread} unread message${u.msg_unread === 1 ? "" : "s"} from other members · ${u.msg_received} received in total — click to read`} className="relative shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full bg-[#FFF4F0] text-[#F5551E] hover:bg-[#ffe4da] transition-colors">
                    <MessageCircle size={15} />
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#F5551E] text-white text-[9px] font-bold flex items-center justify-center" data-testid={`user-msg-unread-count-${u.user_id}`}>{u.msg_unread > 99 ? "99+" : u.msg_unread}</span>
                  </button>
                ) : (
                  <button onClick={() => setConvUser(u)} data-testid={`user-msg-indicator-${u.user_id}`} title={`All ${u.msg_received} message${u.msg_received === 1 ? "" : "s"} from other members have been read — click to read`} className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full bg-slate-100 text-slate-400 hover:bg-slate-200 transition-colors">
                    <MessageCircle size={15} />
                  </button>
                ))}
                {status !== "active" && <Badge className={`${STATUS_STYLE[status]} text-white capitalize`} data-testid={`status-badge-${u.user_id}`}>{status}</Badge>}
                {u.trust && <span data-testid={`user-trust-chip-${u.user_id}`} title={`Trust score ${u.trust.score}/100 · ${u.trust.label}`} className={`text-[10px] font-bold rounded-full px-2 py-0.5 hidden sm:inline-flex items-center gap-1 shrink-0 ${TRUST_CLS[u.trust.color] || TRUST_CLS.amber}`}><ShieldCheck size={11} /> {u.trust.score}</span>}
                <Badge
                  data-testid={`presence-badge-${u.user_id}`}
                  className={`gap-1.5 border-0 ${u.presence === "active" ? "bg-emerald-500 text-white" : u.presence === "away" ? "bg-amber-100 text-amber-700" : "bg-red-50 text-red-600"}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${u.presence === "active" ? "bg-white" : u.presence === "away" ? "bg-amber-500" : "bg-red-500"}`} />
                  {u.presence === "active" ? "Online" : u.presence === "away" ? "Away" : "Offline"}
                </Badge>
                {!u.online && u.last_seen && (
                  <span data-testid={`last-seen-${u.user_id}`} title={`Last active ${new Date(u.last_seen).toLocaleString()}`} className="text-[11px] text-slate-400 hidden md:inline-flex items-center gap-1 shrink-0">
                    <Clock size={11} /> {timeAgo(u.last_seen)}
                  </span>
                )}
                <Badge variant="outline" className="capitalize hidden sm:inline-flex" data-testid={`role-badge-${u.user_id}`}>{u.role}</Badge>
                {u.user_id !== me?.user_id && (
                  <Button onClick={() => openMsgSingle(u)} variant="outline" size="sm" data-testid={`message-user-${u.user_id}`} className="rounded-lg gap-1.5 text-[#F5551E] border-[#F5551E]/40 hover:bg-[#FFF4F0] shrink-0">
                    <MessageCircle size={14} /> <span className="hidden sm:inline">Message</span>
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" data-testid={`user-actions-${u.user_id}`} className="rounded-lg shrink-0"><MoreVertical size={16} /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 bg-white">
                    <DropdownMenuItem onClick={() => setEditUser(u)} data-testid={`edit-user-${u.user_id}`}><Pencil size={15} className="mr-2" /> Edit profile</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => makeVerifiedSeller(u)} data-testid={`verify-seller-${u.user_id}`} className="text-emerald-600"><BadgeCheck size={15} className="mr-2" /> {u.verified_seller ? "Verified seller ✓" : "Make verified seller"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => makeVerifiedBusiness(u)} data-testid={`verify-business-${u.user_id}`} className="text-[#F5551E]"><BadgeCheck size={15} className="mr-2" /> {u.verified_business ? "Verified business ✓" : "Make verified business"}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => makeVerifiedBreeder(u)} data-testid={`verify-breeder-${u.user_id}`} className="text-purple-700"><BadgeCheck size={15} className="mr-2" /> {u.verified_breeder ? "Verified breeder ✓" : "Make verified breeder"}</DropdownMenuItem>
                    {(u.verified_seller || u.verified_business || u.is_verified_seller || u.verified_breeder) && <DropdownMenuItem onClick={() => setUnverifyTarget(u)} data-testid={`unverify-${u.user_id}`} className="text-amber-600"><BadgeX size={15} className="mr-2" /> Remove all verification</DropdownMenuItem>}
                    {u.role !== "admin" && (
                      <DropdownMenuItem onClick={() => loginAsUser(u)} data-testid={`impersonate-${u.user_id}`}><Eye size={15} className="mr-2" /> Log in as user</DropdownMenuItem>
                    )}
                    {(u.role === "admin" || u.role === "moderator") && u.user_id !== me?.user_id && (
                      <DropdownMenuItem onClick={() => forceLogout(u)} data-testid={`force-logout-${u.user_id}`} className="text-amber-600"><LogOut size={15} className="mr-2" /> Force logout</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1 text-xs text-slate-400">Role</div>
                    <DropdownMenuItem onClick={() => changeRole(u.user_id, "user")}>Set as User</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => changeRole(u.user_id, "moderator")}>Set as Moderator</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => changeRole(u.user_id, "admin")}>Set as Admin</DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {status !== "active" && <DropdownMenuItem onClick={() => changeStatus(u.user_id, "active")} data-testid={`activate-${u.user_id}`} className="text-emerald-600"><PlayCircle size={15} className="mr-2" /> Reactivate</DropdownMenuItem>}
                    {status !== "suspended" && <DropdownMenuItem onClick={() => changeStatus(u.user_id, "suspended")} data-testid={`suspend-${u.user_id}`} className="text-amber-600"><PauseCircle size={15} className="mr-2" /> Suspend</DropdownMenuItem>}
                    {status !== "blocked" && <DropdownMenuItem onClick={() => changeStatus(u.user_id, "blocked")} data-testid={`block-${u.user_id}`} className="text-red-600"><Ban size={15} className="mr-2" /> Block</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <div className="relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm text-red-600 hover:bg-slate-100" data-testid={`delete-user-${u.user_id}`}><Trash2 size={15} className="mr-2" /> Delete user</div>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-white">
                        <AlertDialogHeader><AlertDialogTitle>Delete {u.name}?</AlertDialogTitle><AlertDialogDescription>This permanently deletes the user and all their listings, messages, reviews and favorites. This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => removeUser(u.user_id)} data-testid={`confirm-delete-user-${u.user_id}`} className="rounded-xl bg-red-600 hover:bg-red-700">Delete permanently</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
            });
          })()}
        </div>
      )}
      <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={() => { setEditUser(null); load(); }} />
      <AlertDialog open={!!unverifyTarget} onOpenChange={(o) => !o && setUnverifyTarget(null)}>
        <AlertDialogContent className="bg-white" data-testid="unverify-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove verification from {unverifyTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>This removes their verified badge and they will no longer count as a verified seller. You can re-verify them at any time. The user is not notified.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => unverifyTarget && removeVerification(unverifyTarget)} data-testid="confirm-unverify" className="rounded-xl bg-amber-600 hover:bg-amber-700">Remove verification</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <CreateUserDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => load()} />

      <Dialog open={!!msgTarget} onOpenChange={(o) => !o && setMsgTarget(null)}>
        <DialogContent className="sm:max-w-md max-h-[92vh] overflow-y-auto bg-white" data-testid="message-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2">
              <MessageCircle size={18} className="text-[#F5551E]" />
              {msgTarget?.type === "bulk" ? `Message ${msgTarget.count} users`
                : msgTarget?.type === "filter" ? "Message by filter"
                : `Message ${msgTarget?.u?.name}`}
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              {msgTarget?.type === "bulk"
                ? `Sends the same in-site message to ${msgTarget.count} selected users. Each can reply privately.`
                : msgTarget?.type === "filter"
                ? "Sends the same in-site message to everyone matching the filters below."
                : `Sends a direct in-site message to ${msgTarget?.u?.name}. They can reply, and the thread appears in your Conversations.`}
            </DialogDescription>
          </DialogHeader>
          {msgTarget?.type === "filter" && (
            <div className="grid grid-cols-2 gap-2" data-testid="audience-filters">
              <div>
                <Label className="text-xs text-slate-500">Seller type</Label>
                <Select value={filters.seller_type} onValueChange={(v) => setFilter("seller_type", v)}>
                  <SelectTrigger data-testid="filter-seller-type" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["any", "Individual", "Breeder", "Business", "Shelter"].map((t) => <SelectItem key={t} value={t}>{t === "any" ? "Any" : t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Province</Label>
                <Select value={filters.province} onValueChange={(v) => setFilter("province", v)}>
                  <SelectTrigger data-testid="filter-province" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    <SelectItem value="any">Any province</SelectItem>
                    {[["ON","Ontario"],["BC","British Columbia"],["AB","Alberta"],["QC","Quebec"],["MB","Manitoba"],["SK","Saskatchewan"],["NS","Nova Scotia"],["NB","New Brunswick"],["NL","Newfoundland & Labrador"],["PE","Prince Edward Island"],["YT","Yukon"],["NT","Northwest Territories"],["NU","Nunavut"]].map(([v,n]) => <SelectItem key={v} value={v}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Verified</Label>
                <Select value={filters.verified} onValueChange={(v) => setFilter("verified", v)}>
                  <SelectTrigger data-testid="filter-verified" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["any", "verified", "unverified"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t === "any" ? "Any" : t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilter("status", v)}>
                  <SelectTrigger data-testid="filter-status" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {["any", "active", "suspended", "blocked"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t === "any" ? "Any" : t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {msgTarget?.type === "filter" && (
            <div className="flex items-center justify-between gap-2 text-sm text-[#12234A] bg-[#FFF4F0] rounded-xl px-3 py-2" data-testid="audience-count">
              <span><strong>{audCount === null ? "…" : Math.max(0, audCount - excludeIds.length)}</strong> user{(audCount - excludeIds.length) === 1 ? "" : "s"} will receive this{excludeIds.length ? ` · ${excludeIds.length} excluded` : ""}</span>
              <button type="button" onClick={openPreview} disabled={!audCount} data-testid="preview-audience-btn" className="text-xs font-semibold text-[#F5551E] hover:underline disabled:opacity-40 disabled:no-underline shrink-0">Preview & edit list</button>
            </div>
          )}
          {msgTarget?.type === "filter" && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="saved-audiences">
              {savedAudiences.map((a) => (
                <span key={a.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border border-slate-200 bg-slate-50" data-testid={`saved-audience-${a.id}`}>
                  <button type="button" onClick={() => loadSavedAudience(a)} className="text-slate-600 hover:text-[#F5551E]">{a.name}</button>
                  <button type="button" onClick={() => deleteAudience(a.id)} data-testid={`saved-audience-del-${a.id}`} className="text-slate-400 hover:text-red-500">×</button>
                </span>
              ))}
              <button type="button" onClick={saveAudience} data-testid="save-audience-btn" className="text-xs font-medium text-[#F5551E] hover:underline">+ Save this audience</button>
            </div>
          )}
          {templates.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="message-templates">
              {templates.map((t) => (
                <button key={t.id} type="button" onClick={() => applyTemplate(t)} data-testid={`message-template-${t.id}`}
                  className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 bg-white text-slate-600 hover:border-[#F5551E] hover:text-[#F5551E] transition-colors">
                  {t.label}
                </button>
              ))}
            </div>
          )}
          <Textarea
            value={msgText}
            onChange={(e) => setMsgText(e.target.value)}
            data-testid="message-user-text"
            rows={5}
            maxLength={2000}
            placeholder={msgTarget?.type === "single" ? `Write a message to ${msgTarget?.u?.name || "this user"}…` : "Write a message to all matching users…"}
            className="rounded-xl resize-none"
          />
          <div className="flex items-center justify-between gap-2">
            <button type="button" onClick={saveTemplate} disabled={!msgText.trim()} data-testid="save-template-btn" className="text-xs font-medium text-[#F5551E] hover:underline disabled:opacity-40 disabled:no-underline">
              + Save as template
            </button>
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="message-email-label">
            <Checkbox checked={msgEmail} onCheckedChange={(v) => setMsgEmail(!!v)} data-testid="message-email-toggle" />
            <span className="text-sm text-slate-700">Also email {msgTarget?.type === "single" ? "the user" : "them"} a copy</span>
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="message-schedule-label">
            <Checkbox checked={schedOn} onCheckedChange={(v) => setSchedOn(!!v)} data-testid="message-schedule-toggle" />
            <span className="text-sm text-slate-700">Schedule for later</span>
          </label>
          {schedOn && (
            <Input type="datetime-local" value={schedAt} onChange={(e) => setSchedAt(e.target.value)} data-testid="message-schedule-at" className="h-10 rounded-xl" />
          )}
          {schedOn && msgTarget?.type === "filter" && (
            <div>
              <Label className="text-xs text-slate-500">Repeat</Label>
              <Select value={recurrence} onValueChange={setRecurrence}>
                <SelectTrigger data-testid="message-recurrence" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="none">One time</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              {recurrence !== "none" && <p className="text-xs text-slate-400 mt-1">Re-sends to whoever matches these filters at each run.</p>}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setMsgTarget(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={sendMessage} disabled={sendingMsg || !msgText.trim() || (msgTarget?.type === "filter" && !audCount)} data-testid="message-user-send" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">
              {sendingMsg ? <Loader2 size={15} className="animate-spin" /> : schedOn ? <Clock size={15} /> : <Send size={15} />}
              {schedOn ? "Schedule" : msgTarget?.type === "single" ? "Send message" : `Send${msgTarget?.type === "bulk" ? ` to ${msgTarget.count}` : audCount ? ` to ${Math.max(0, audCount - excludeIds.length)}` : ""}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <CampaignDrillDialog campaign={drillCampaign} onClose={() => setDrillCampaign(null)} onFollowUp={(r) => { setDrillCampaign(null); openMsgSingle({ user_id: r.user_id, name: r.name }); }} />

      <Dialog open={previewOpen} onOpenChange={(o) => !o && setPreviewOpen(false)}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto bg-white" data-testid="audience-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Users size={18} className="text-[#F5551E]" /> Audience preview</DialogTitle>
            <DialogDescription className="text-slate-500">{previewRows === null ? "Resolving…" : `Untick anyone you want to skip. ${previewRows.filter((u) => !excludeIds.includes(u.user_id)).length} of ${previewRows.length} will receive this message.`}</DialogDescription>
          </DialogHeader>
          {previewRows === null ? <Skeleton className="h-40 rounded-xl" /> : (
            <div className="space-y-1">
              {previewRows.map((u) => {
                const included = !excludeIds.includes(u.user_id);
                return (
                  <label key={u.user_id} className={`flex items-center gap-2 text-sm border-b border-slate-100 py-1.5 cursor-pointer ${included ? "" : "opacity-40"}`} data-testid={`preview-row-${u.user_id}`}>
                    <Checkbox checked={included} onCheckedChange={(v) => setExcludeIds((s) => v ? s.filter((x) => x !== u.user_id) : [...s, u.user_id])} data-testid={`preview-check-${u.user_id}`} />
                    <span className="flex-1 min-w-0 truncate">
                      <span className="font-medium text-[#12234A]">{u.name}</span>
                      <span className="text-xs text-slate-400 ml-1">{u.email}</span>
                    </span>
                    {u.seller_type && <Badge className={`${sellerTypeClass(u.seller_type)} text-[10px] shrink-0 border-0`}>{u.seller_type}</Badge>}
                    {u.province && <span className="text-xs text-slate-400 shrink-0">{u.province}</span>}
                    {u.is_verified_seller && <BadgeCheck size={13} className="text-emerald-500 shrink-0" />}
                  </label>
                );
              })}
            </div>
          )}
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-xl" data-testid="preview-done">Done</Button>
          </div>
        </DialogContent>
      </Dialog>

      <QuietHoursDialog open={quietOpen} initial={quiet} onClose={() => setQuietOpen(false)} onSaved={setQuiet} />
    </div>
  );
}
